import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiSearch, FiUser, FiMail, FiBriefcase, FiPlay } from 'react-icons/fi';
import simulationService from '../services/simulationService';
import { useAuth } from '../contexts/AuthContext';

const UserSimulationModal = ({ isOpen, onClose }) => {
  const { enterSimulation } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const delayDebounceFn = setTimeout(() => {
        loadUsers();
      }, 300);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await simulationService.listUsers(searchTerm);
      setUsers(response.users || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      alert('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSimulation = async (user) => {
    try {
      setLoading(true);
      const response = await simulationService.startSimulation(user.id);

      if (!response?.success) {
        throw new Error(response?.message || 'Falha ao iniciar simulação');
      }

      await enterSimulation(user.id, {
        name: user.name,
        email: user.email,
        cargo: user.cargo,
        color: user.color
      }, response.simulation?.projectIds);

      onClose();
    } catch (error) {
      console.error('Erro ao iniciar simulação:', error);
      alert('Erro ao iniciar simulação: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <h2>Simular Usuário</h2>
            <p>Selecione um usuário para simular suas permissões e acessos</p>
          </div>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <SearchContainer>
          <FiSearch />
          <SearchInput
            type="text"
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <UserList>
          {loading ? (
            <LoadingState>Carregando usuários...</LoadingState>
          ) : users.length === 0 ? (
            <EmptyState>Nenhum usuário encontrado</EmptyState>
          ) : (
            users.map((user) => (
              <UserCard
                key={user.id}
                onClick={() => setSelectedUser(user)}
                $selected={selectedUser?.id === user.id}
              >
                <UserAvatar $color={user.color || '#3b82f6'}>
                  <FiUser />
                </UserAvatar>
                <UserInfo>
                  <UserName>{user.name}</UserName>
                  <UserDetails>
                    <DetailItem>
                      <FiMail />
                      {user.email}
                    </DetailItem>
                    {user.cargo && (
                      <DetailItem>
                        <FiBriefcase />
                        {user.cargo}
                      </DetailItem>
                    )}
                  </UserDetails>
                </UserInfo>
                <SimulateButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartSimulation(user);
                  }}
                  disabled={loading}
                >
                  <FiPlay />
                  Simular
                </SimulateButton>
              </UserCard>
            ))
          )}
        </UserList>

        {selectedUser && (
          <ModalFooter>
            <FooterInfo>
              <strong>Usuário selecionado:</strong> {selectedUser.name}
            </FooterInfo>
            <StartButton
              onClick={() => handleStartSimulation(selectedUser)}
              disabled={loading}
            >
              <FiPlay />
              Iniciar Simulação
            </StartButton>
          </ModalFooter>
        )}
      </ModalContainer>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2em;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid ${({ theme }) => theme.border};
  animation: modalFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5em 2em;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  h2 {
    margin: 0 0 0.3em 0;
    font-size: 1.3em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0;
    font-size: 0.9em;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${({ theme }) => theme.buttonBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.2em;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: #ef4444;
    color: #ef4444;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 1em 2em;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card2};

  svg {
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    font-size: 1.1em;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.text};
  font-size: 0.95em;

  &::placeholder {
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
  }
`;

const UserList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1em;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border};
    border-radius: 4px;

    &:hover {
      background: ${({ theme }) => theme.text}40;
    }
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 1em;
  background: ${({ theme, $selected }) => 
    $selected ? theme.card2 : theme.card};
  border: 1px solid ${({ theme, $selected }) => 
    $selected ? theme.primary : theme.border};
  border-radius: 10px;
  margin-bottom: 0.8em;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.card2};
    transform: translateX(4px);
  }
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%;
  background: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3em;
`;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3em;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 0.95em;
  color: ${({ theme }) => theme.text};
`;

const UserDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.85em;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;

  svg {
    font-size: 0.9em;
  }
`;

const SimulateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.primary};
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 0.85em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.primary}dd;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1em;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1em 2em;
  border-top: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card2};
`;

const FooterInfo = styled.div`
  font-size: 0.9em;
  color: ${({ theme }) => theme.text};

  strong {
    font-weight: 600;
  }
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${({ theme }) => theme.primary};
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.95em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.primary}dd;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.primary}40;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1.1em;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3em;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3em;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
`;

export default UserSimulationModal;
